/* Outbox drain, plan cache and content prefetch (03 §3.3–3.6).
 *
 * Two behaviours here are easy to get subtly wrong and are called out:
 *
 *  1. `duplicate` is a SUCCESS. Acknowledged means accepted OR duplicate — that
 *     is what makes a retry after a lost response safe.
 *  2. A changed `state_epoch` means the server was restored from a backup and
 *     may have lost recently acknowledged events. We then re-POST the whole
 *     14-day archive. Safe by idempotency; anything the server still holds
 *     collapses back to `duplicate`.
 */

import { TIMEZONE } from '@ascent/shared';
import { api, ApiFailure, type TodayPlan, type UnitDto } from './api';
import {
  acknowledge, appendEvent, clearUnitCache, getMeta, getUnit, putUnit, readArchive,
  readOutbox, setMeta, type OutboxEvent,
} from './db';

const META_EPOCH = 'state_epoch';
const META_RELEASE = 'active_release_id';
const META_PLAN = 'today_plan';
const META_PLAN_DAY = 'today_plan_day';
const META_LAST_SYNC = 'last_sync_at';

/** Strip local bookkeeping before sending; the server ignores unknown fields
 * anyway, but sending less is cheaper and clearer. */
function wire(e: OutboxEvent) {
  return {
    event_id: e.event_id,
    type: e.type,
    occurred_at: e.occurred_at,
    schema_version: e.schema_version,
    payload: e.payload,
  };
}

export interface SyncResult {
  sent: number;
  acknowledged: number;
  rejected: number;
  epochChanged: boolean;
  offline: boolean;
}

export async function drainOutbox(): Promise<SyncResult> {
  const pending = await readOutbox();
  const result: SyncResult = {
    sent: 0, acknowledged: 0, rejected: 0, epochChanged: false, offline: false,
  };
  if (pending.length === 0) return result;

  // Oldest first, in chunks under the 500-event batch limit.
  const ordered = [...pending].sort((a, b) => a.queued_at.localeCompare(b.queued_at));
  const chunks: OutboxEvent[][] = [];
  for (let i = 0; i < ordered.length; i += 200) chunks.push(ordered.slice(i, i + 200));

  for (const chunk of chunks) {
    let response;
    try {
      response = await api.postReviews(chunk.map(wire));
    } catch (err) {
      if (err instanceof ApiFailure && err.code === 'OFFLINE') {
        result.offline = true;
        return result;
      }
      throw err;
    }

    result.sent += chunk.length;

    // accepted AND duplicate are both acknowledged — a duplicate is a success.
    const done = response.results
      .filter((r) => r.status === 'accepted' || r.status === 'duplicate')
      .map((r) => r.event_id);
    // A rejected event is dropped, never retried forever (05 §1.5.4).
    const rejected = response.results.filter((r) => r.status === 'rejected').map((r) => r.event_id);

    await acknowledge([...done, ...rejected]);
    result.acknowledged += done.length;
    result.rejected += rejected.length;

    const epochChanged = await reconcileEpoch(response.sync.state_epoch);
    if (epochChanged) result.epochChanged = true;

    await reconcileRelease(response.sync.active_release_id);
  }

  return result;
}

/**
 * Detect a restore-from-backup and replay the retained archive.
 * Returns true when the epoch changed.
 */
async function reconcileEpoch(serverEpoch: string): Promise<boolean> {
  const known = await getMeta<string>(META_EPOCH);
  if (known === serverEpoch) return false;

  await setMeta(META_EPOCH, serverEpoch);
  if (known === undefined) return false; // first sync, nothing to replay

  const archive = await readArchive();
  if (archive.length === 0) return true;

  // Re-POST everything we still hold. Idempotency makes this safe.
  for (let i = 0; i < archive.length; i += 200) {
    try {
      await api.postReviews(archive.slice(i, i + 200).map(wire));
    } catch {
      break; // offline or failing: the next successful sync retries
    }
  }
  return true;
}

/** A new active release invalidates cached Units (03 §3.6). */
async function reconcileRelease(activeReleaseId: string | null): Promise<void> {
  const known = await getMeta<string | null>(META_RELEASE);
  if (known === activeReleaseId) return;
  await setMeta(META_RELEASE, activeReleaseId);
  if (known !== undefined) await clearUnitCache();
}

// ---- Today plan -----------------------------------------------------------

export async function fetchAndCachePlan(): Promise<TodayPlan | null> {
  try {
    const plan = await api.today();
    await setMeta(META_PLAN, plan);
    await setMeta(META_PLAN_DAY, plan.day);
    // Stamped only on a real server round-trip, so Settings reports when this
    // device last actually reached the server — not when it last tried.
    await setMeta(META_LAST_SYNC, new Date().toISOString());
    await reconcileRelease(plan.active_release_id);
    void prefetchUnits(plan.units_to_prefetch);
    return plan;
  } catch (err) {
    if (err instanceof ApiFailure && (err.code === 'OFFLINE' || err.status === 503)) {
      return getCachedPlan();
    }
    throw err;
  }
}

export async function getCachedPlan(): Promise<TodayPlan | null> {
  return (await getMeta<TodayPlan>(META_PLAN)) ?? null;
}

export async function getLastSyncAt(): Promise<string | null> {
  return (await getMeta<string>(META_LAST_SYNC)) ?? null;
}

/**
 * A cached plan from an earlier calendar day is STALE but still servable
 * (03 §3.6): the drill player keeps walking the cached queue and the projected
 * next-day queue. The client never invents a due date — it only ever consumes
 * server-computed ones.
 */
export async function isPlanStale(plan: TodayPlan | null): Promise<boolean> {
  if (!plan) return true;
  const cachedDay = await getMeta<string>(META_PLAN_DAY);
  const localDay = activityDay();
  return !!cachedDay && cachedDay !== localDay ? true : plan.day !== localDay;
}

/**
 * Today's date in the activity timezone — the SAME day the server stamps on a
 * plan. This used to read the UTC date, which meant that between midnight and
 * 02:00 Amsterdam the two never matched and the app permanently claimed to be
 * "showing yesterday's plan" while holding a plan generated minutes earlier.
 */
function activityDay(): string {
  // 'en-CA' is the locale whose short date is already ISO 8601.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

/** Eagerly cache the Units today's session needs, so airplane mode works. */
export async function prefetchUnits(unitIds: string[]): Promise<void> {
  for (const id of unitIds) {
    try {
      const unit = await api.unit(id);
      await putUnit({ unit_id: id, content_hash: String(unit.version), body: unit });
    } catch {
      // Best effort: a Unit that fails to prefetch simply is not playable offline.
    }
  }
}

/**
 * Resolve a Unit for playback: cache first, then network.
 *
 * The network fallback matters because prefetch is fire-and-forget — starting a
 * session seconds after the first sync would otherwise find an empty cache and
 * show "nothing to play" while online. Offline, the cache is all there is, and a
 * miss correctly surfaces as "not cached yet".
 */
export async function cachedUnit(unitId: string): Promise<UnitDto | null> {
  const row = await getUnit(unitId);
  if (row?.body) return row.body as UnitDto;

  if (!navigator.onLine) return null;
  try {
    const unit = await api.unit(unitId);
    await putUnit({ unit_id: unitId, content_hash: String(unit.version), body: unit });
    return unit;
  } catch {
    return null;
  }
}

/** Queue a graded answer. Callers MUST await this before advancing the UI. */
export function queueReview(
  cardId: string, unitId: string, grade: 1 | 2 | 3 | 4, durationMs: number,
): Promise<OutboxEvent> {
  return appendEvent('review', {
    card_id: cardId, unit_id: unitId, grade, duration_ms: durationMs,
  });
}

const META_COMPLETED = 'completed_units';

/**
 * Queue a lesson completion. Idempotent locally: first completion wins, matching
 * the server rule that a later completion of the same Unit returns `duplicate`.
 * The event is committed BEFORE the local "completed" marker, so a crash between
 * the two loses the checkmark rather than the event.
 */
export async function queueUnitCompletion(unitId: string): Promise<OutboxEvent | null> {
  if (await isUnitCompleted(unitId)) return null;
  const event = await appendEvent('unit_completion', { unit_id: unitId });
  const completed = (await getMeta<string[]>(META_COMPLETED)) ?? [];
  await setMeta(META_COMPLETED, [...completed, unitId]);
  return event;
}

export async function isUnitCompleted(unitId: string): Promise<boolean> {
  const completed = (await getMeta<string[]>(META_COMPLETED)) ?? [];
  return completed.includes(unitId);
}
